<template>
  <PaymentNotifications />
  <ContainerLayout class="tw:flex tw:flex-col tw:my-6 tw:px-4">
    <div class="tw:flex tw:justify-start tw:mb-6">
      <router-link
        class="tw:hover:cursor-pointer tw:font-bold tw:text-blue-700 tw:flex tw:items-end"
        :to="{ name: 'studio-plus-compare' }"
      >
        <ChevronRight class="tw:rotate-180 tw:size-[18px]" />
        <span>{{ $t('setup_back') }}</span>
      </router-link>
    </div>
    <h2 class="tw:text-2xl tw:font-bold tw:mb-6">
      {{ $t('business_studio_plus_checkout_subscription_header') }}
    </h2>
    <div v-if="isLoadingRequiredData">
      <div class="tw:animate-pulse tw:w-full tw:rounded-lg tw:bg-gray-300 tw:h-[300px] tw:mb-4 tw:mt-8"></div>
      <div class="tw:animate-pulse tw:w-full tw:rounded-lg tw:bg-gray-300 tw:h-[300px] tw:mb-4 tw:mt-8"></div>
    </div>
    <div v-else-if="isRequiredDataError" class="tw:mb-4 tw:mt-8">
      <p class="tw:text-center tw:text-xl">{{ $t('onboarding.appLaunchError.header') }}</p>
    </div>
    <div v-else class="sp-payment-grid">
      <div class="sp-payment-accommodation">
        <PaymentCurrencySelector v-if="!zuoraAccountData?.billingAndPayment?.currency" />
      </div>
      <div class="sp-payment-form">
        <PaymentForm
          class="tw:mb-6"
          :key="selectedAccommodationId"
          ref="paymentFormRef"
          :currency="currency"
          :accommodationId="selectedAccommodationId"
          :productName="zuoraMetricConstants.productName.plus"
          @onFormValidationSuccess="onFormValidationSuccess"
          @onFormValidationError="onFormValidationError"
          @onZuoraHostedPageLoadError="onFormValidationError"
        />
      </div>
      <div class="sp-payment-footer tw:mb-6">
        <div class="sticky">
          <h3 class="tw:font-bold tw:mb-1">{{ $t('business_studio_plus_checkout_summary') }}</h3>
          <BaseCard noPadding class="tw:px-4 tw:py-6">
            <SubscriptionPriceSummary
              :price="studioPlusPrice"
              :loading="isLoading"
              :fetching="isFetching"
              @applyVoucherCode="applyVoucherCode"
            />
            <div class="tw:flex tw:gap-3 tw:items-center tw:mb-6">
              <input
                id="studio-plus-terms"
                type="checkbox"
                class="tw:hover:cursor-pointer tw:h-4 tw:w-4"
                :value="termsAccepted"
                @change="e => (termsAccepted = e.target.checked)"
                data-testid="studio-plus-terms-checkbox"
              />
              <label
                for="studio-plus-terms"
                class="tw:grow tw:text-sm tw:leading-4 tw:mb-0 terms"
                v-html="$t('business_studio_plus_terms_and_conditions_checkbox')"
              />
            </div>
            <MainButton
              class="tw:w-full tw:mb-6"
              :disabled="!paymentEnabled"
              @click="onStartFreeTrial"
              dataTestId="plus-checkout-page-start-subscription-button"
              >{{ $t('check_out_page_plus_cta') }}</MainButton
            >
            <p class="tw:flex tw:gap-3 tw:mb-4">
              <Lock class="tw:size-6" />{{ $t('checkoutBasket.detailsEntry.secureConnection.recurringFee') }}
            </p>
            <p class="tw:mb-4 tw:flex tw:gap-3">
              <CircleInfo class="tw:fill-none tw:size-6" />
              {{ $t('checkout_subscription_tax_notification') }}
            </p>
            <p class="tw:mb-4 tw:flex tw:gap-3">
              <Update class="tw:size-6" />
              {{ $t('business_studio_plus_subscription_renewal_info_annual') }}
            </p>
            <p class="tw:mb-4 tw:flex tw:gap-3">
              <GearWheel class="tw:size-6" />
              {{ $t('business_studio_plus_checkout_info_text') }}
            </p>
            <p class="tw:flex tw:gap-3">
              <TagOutlineIcon class="tw:size-6" />
              {{ $t('check_out_discount_disclaimer') }}
            </p>
          </BaseCard>
        </div>
      </div>
    </div>
    <FullScreenLoader v-show="isSubmittingNewSubscription || isFetchingRequiredData" />
  </ContainerLayout>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { useRouter } from 'vue-router'
import { useQueryClient } from '@tanstack/vue-query'

import BaseCard from '@/components/dashboard/BaseCard.vue'
import ChevronRight from '@/components/icons/ChevronRight.svg?component'
import TagOutlineIcon from '@/components/icons/TagOutlineIcon.svg?component'
import GearWheel from '@/components/icons/GearWheel.svg?component'
import Lock from '@/components/icons/Lock.svg?component'
import CircleInfo from '@/components/icons/CircleInfo.svg?component'
import Update from '@/components/icons/Update.svg?component'
import FullScreenLoader from '@/components/base/loaders/FullScreenLoader.vue'
import MainButton from '@/components/base/buttons/MainButton.vue'
import PaymentForm from '@/components/payment/PaymentForm.vue'
import SubscriptionPriceSummary from '@/components/studioPlus/SubscriptionPriceSummary.vue'
import PaymentCurrencySelector from '@/components/studioPlus/PaymentCurrencySelector.vue'
import PaymentNotifications from '@/components/notifications/PaymentNotifications.vue'
import {
  useAvailablePaymentMethods,
  useExistingPaymentMethods,
  useZuoraAccount,
  useStudioPlusNewSubscriptionMutation,
  useStudioPlusPrice,
  useErrorMetrics,
  useStudioPlusCampaign,
} from '@/components/payment/queries'
import { selectedAccommodationId, useUserDetails, useAccommodationsByOrgId } from '@/layouts/queries'
import { useCampaign } from '@/components/rateConnect/queries'

import * as Sentry from '@sentry/vue'
import appAnalytics from '@/utils/tracking'
import zuoraMetricConstants from '@/constants/zuoraMetricConstants'
import usePaymentNotifications from '@/components/notifications/usePaymentNotifications'
import ContainerLayout from '@/layouts/ContainerLayout.vue'

import { getSupportCenterUrl } from '@/utils/Utility.ts'
import { useScrollTo } from '@/composables/useScrollTo'
import { useSessionValues } from '@/composables/useSessionValues'
import { useFormatting } from '@/composables/useFormatting'

const router = useRouter()
const queryClient = useQueryClient()
const { formatDate } = useFormatting()
const { scrollToTop } = useScrollTo()
const { displayPaymentNotification, clearAllNotifications } = usePaymentNotifications()
const { sendErrorMetrics } = useErrorMetrics()

const capturePaymentError = (message, extra = {}) => {
  Sentry.withScope(scope => {
    scope.setTag('feature', 'studio-plus-subscription')
    scope.setContext('subscriptionDetails', {
      accommodationId: selectedAccommodationId.value,
      currency: currency.value,
      ...extra,
    })
    Sentry.captureMessage(message, 'error')
  })
}

const { currency, hasAdminOrSupport, language, selectedOrganisationId, userId } = useSessionValues()
const { userDetails } = useUserDetails(userId)
const voucherCode = ref('')

const { isError: isAvailablePaymentMethodsError } = useAvailablePaymentMethods(selectedAccommodationId, currency)
const {
  existingPaymentMethodsLoading: isExistingPaymentMethodsLoading,
  isFetching: isExistingPaymentMethodsFetching,
  isError: isExistingPaymentMethodsError,
  existingPaymentMethods,
} = useExistingPaymentMethods(selectedAccommodationId)
const {
  zuoraAccountData,
  isLoading: isZuoraAccountLoading,
  isFetching: isZuoraAccountFetching,
  isError: isZuoraAccountError,
} = useZuoraAccount()
const { sendStudioPlusNewSubscription } = useStudioPlusNewSubscriptionMutation()

const isLoadingRequiredData = computed(() => isExistingPaymentMethodsLoading.value || isZuoraAccountLoading.value)
const isFetchingRequiredData = computed(() => isExistingPaymentMethodsFetching.value || isZuoraAccountFetching.value)
const isRequiredDataError = computed(
  () => isAvailablePaymentMethodsError.value || isExistingPaymentMethodsError.value || isZuoraAccountError.value
)
const { campaignsData } = useCampaign(selectedAccommodationId)
const { studioPlusCampaign } = useStudioPlusCampaign(selectedAccommodationId)
const isPaidClient = computed(
  () =>
    Boolean(studioPlusCampaign.value) ||
    (Array.isArray(campaignsData.value) ? campaignsData.value.length > 0 : Boolean(campaignsData.value))
)

const paymentFormRef = ref(null)
const termsAccepted = ref(false)
const isSubmittingNewSubscription = ref(false)

const { currentAccommodation } = useAccommodationsByOrgId(selectedOrganisationId)

const applyVoucherCode = code => {
  voucherCode.value = code
}

const paymentEnabled = computed(() => {
  const selectedMethod = existingPaymentMethods.value?.find(
    item => item.id === paymentFormRef.value?.selectedPaymentMethod
  )
  const isWireTransfer = selectedMethod?.type === 'WIRE_TRANSFER'

  return (
    termsAccepted.value &&
    paymentFormRef.value?.selectedPaymentMethod !== null &&
    paymentFormRef.value?.isBillingFormValid &&
    !isWireTransfer &&
    studioPlusPrice.value
  )
})

const formZipCode = computed(() => paymentFormRef.value?.billingForm?.VATrequiredData.zipCode)
const formCountry = computed(() => paymentFormRef.value?.billingForm?.VATrequiredData.country)
const formState = computed(() => paymentFormRef.value?.billingForm?.VATrequiredData.state)
const formTaxId = computed(() => paymentFormRef.value?.billingForm?.VATrequiredData.taxId)
const noTaxId = computed(() => paymentFormRef.value?.billingForm?.VATrequiredData.noTaxId)
const taxId = computed(() => (noTaxId.value ? null : formTaxId.value))
const voucherCodeValue = computed(() => voucherCode.value)

const { studioPlusPrice, isLoading, isFetching } = useStudioPlusPrice({
  selectedAccommodationId,
  countryCode: formCountry,
  postalCode: formZipCode,
  countryState: formState,
  taxId: taxId,
  currency,
  voucherCode: voucherCodeValue,
})

onMounted(() => {
  if (!hasAdminOrSupport.value) {
    router.push({ name: 'studio-plus-compare' })
  }
  appAnalytics.track(appAnalytics.events.SP_PAYMENT_VISITED, {
    item_id: selectedAccommodationId.value,
    itemName: currentAccommodation?.value?.name,
    language: language.value,
    studioUserId: userDetails?.value?.id,
    email: userDetails?.value?.email,
    firstName: userDetails?.value?.firstName,
    lastName: userDetails?.value?.lastName,
  })
})

const onStartFreeTrial = () => {
  clearAllNotifications()
  appAnalytics.track(appAnalytics.events.SP_START_PLUS_SUBSCRIPTION_BUTTON_CLICKED, {
    item_id: selectedAccommodationId.value,
    currency: currency.value,
  })
  isSubmittingNewSubscription.value = true
  paymentFormRef.value.validateForm()
}
const supportCenterUrl = computed(() =>
  getSupportCenterUrl(language.value, userId.value, selectedAccommodationId.value, isPaidClient.value)
)

const onFormValidationSuccess = () => {
  sendStudioPlusNewSubscription(
    {
      accommodationId: selectedAccommodationId.value,
      itemName: currentAccommodation?.value?.name,
      email: userDetails?.value?.email,
      language: language?.value,
      studioUserId: userDetails?.value?.id,
      firstName: userDetails?.value?.firstName,
      lastName: userDetails?.value?.lastName,
      ...(studioPlusPrice?.value?.itemPriceVoucher?.isValid && {
        voucherCode: studioPlusPrice?.value?.itemPriceVoucher?.voucherCode,
      }),
    },
    {
      onSuccess: data => {
        if (data.status === 204 && zuoraAccountData.value) {
          onFormValidationSuccess()
        } else {
          queryClient.invalidateQueries({ queryKey: ['studioPlusCampaign'] })
          appAnalytics.track(appAnalytics.events.SP_PAYMENT_SUCCEED, {
            item_id: selectedAccommodationId.value,
            price: studioPlusPrice.value.baseSubscriptionMonthlyPrice,
            language: language?.value,
            studioUserId: userDetails?.value?.id,
            email: userDetails?.value?.email,
            firstName: userDetails?.value?.firstName,
            lastName: userDetails?.value?.lastName,
            subscription_date: formatDate(data.data.subscriptionStartDate, {
              year: 'numeric',
              month: 'long',
              day: '2-digit',
            }),
            renewal_date: formatDate(data?.data?.nextPaymentDate, {
              year: 'numeric',
              month: 'long',
              day: '2-digit',
            }),
          })
          isSubmittingNewSubscription.value = false
          window.localStorage.setItem(`contact_info_banner_${selectedAccommodationId.value}`, 'true')
          window.dispatchEvent(
            new CustomEvent('contact_info_banner_localstorage-changed', {
              detail: {
                storage: localStorage.getItem(`contact_info_banner_${selectedAccommodationId.value}`),
              },
            })
          )
          router.push({ name: 'studio-plus-insights' })
        }
      },
      onError: error => {
        isSubmittingNewSubscription.value = false
        paymentFormRef.value?.closeNewPaymentMethodForm()
        if (
          error?.response?.data?.message?.includes(`"gatewayResponse":{"code":"907"`) ||
          error?.response?.data?.message?.includes(`"gatewayResponse":{"code":"138"`)
        ) {
          displayPaymentNotification({
            message: 'checkout_errors_payment_method',
            messageTranslationKey: true,
            header: 'payment_method_declined',
          })
          sendErrorMetrics({
            itemId: selectedAccommodationId.value,
            productName: zuoraMetricConstants.productName.plus,
            code: zuoraMetricConstants.metricErrorCodes.paymentError,
            message: error?.response?.data?.message,
          })
          capturePaymentError('Subscription creation failed - payment method declined', {
            gatewayResponse: error?.response?.data?.message,
            status: error?.response?.status,
          })
        } else {
          displayPaymentNotification({
            message: 'campaign_setup_error',
            withSlot: true,
            slotName: 'HotelierSupport',
            linkLabel: 'hotelier_support',
            linkUrl: supportCenterUrl.value,
            header: 'error_message_header',
          })
          capturePaymentError('Subscription creation failed - unknown error', {
            errorMessage: error?.response?.data?.message || error?.message,
            status: error?.response?.status,
          })
        }
        scrollToTop()
      },
    }
  )
}

onBeforeUnmount(() => clearAllNotifications())
const onFormValidationError = () => {
  appAnalytics.track(appAnalytics.events.SP_PAYMENT_FAILED, {
    item_id: selectedAccommodationId.value,
  })
  isSubmittingNewSubscription.value = false
}
</script>

<style scoped>
.sp-payment-grid {
  display: block;

  @media (min-width: 768px) {
    display: grid;
    grid-template-columns: 1.5fr 1fr;
    grid-template-areas:
      'payment-accommodation payment-footer'
      'payment-form payment-footer';
    column-gap: 4rem;
  }
}
.sp-payment-form {
  grid-area: payment-form;
}
.sp-payment-footer {
  grid-area: payment-footer;
}
.sp-payment-accommodation {
  grid-area: payment-accommodation;
}
.terms :deep(a) {
  color: var(--tw-color-blue-700);
}
.sticky {
  position: sticky;
  top: 120px;
  right: 0;
}
</style>
